import { mergeRefs } from '@ipfabric/frontend-shared-assets/utils/react/mergeRefs';
import { nonEmptyArray } from '@ipfabric/utils';
import { eqDeepStrict } from '@ipfabric/utils/fp/comparison';
import { keepIdentityIfEqual } from '@ipfabric/utils/fp/keepIdentityIfEqual';
import classNames from 'classnames';
import {
  type BoxTreeHocProps,
  CHILDREN_ORIENTATION_DEFAULT,
} from 'components/BoxTree/BoxTreeHocProps';
import { BoxTreeLevelConnectionLines } from 'components/BoxTree/BoxTreeLevelConnectionLines';
import type { BoxTreeLevelLayoutInfo } from 'components/BoxTree/BoxTreeLevelLayoutInfo';
import type { BoxTreeNodePath } from 'components/BoxTree/BoxTreeNodeComponentProps';
import { identity, pipe } from 'fp-ts/lib/function';
import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FC, Ref } from 'react';
import { useElementListRef } from 'utils/hooks/useElementListRef';
import { useMemoCollection } from 'utils/hooks/useMemoCollection';

interface Props<TNode, TContext> {
  context: TContext;
  path: BoxTreeNodePath;
  /**
   * To be used only in this component's recursion: pass an observer to child subtrees,
   * that should be mounted to elements that affect connection lines:
   * - child's container element, because resizing the whole subtree moves following subtrees
   * - child's subtree root element, because it affects position of its horizontal connection line
   */
  linesLayoutResizeObserver?: ResizeObserver;
  node: TNode;
  parentNode: TNode | null;
  /** Root node element in the subtree */
  rootRef?: Ref<HTMLDivElement>;
  topLevelLine?: boolean;
}

const attributesByOrientation = {
  horizontal: {
    position: 'offsetLeft',
    size: 'offsetWidth',
  },
  vertical: {
    position: 'offsetTop',
    size: 'offsetHeight',
  },
} as const;

export const makeBoxSubtree = <TNode, TContext = undefined>(
  hocProps: BoxTreeHocProps<TNode, TContext>,
): FC<Props<TNode, TContext>> => {
  const useChildrenContext = hocProps.useChildrenContext ?? identity;
  const BoxSubtree: FC<Props<TNode, TContext>> = (props) => {
    const children = hocProps.getChildren(props.node, props.context);

    const rootRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [childRootRefs, setChildRootRef] = useElementListRef<
      number,
      HTMLDivElement
    >();
    const [layoutInfo, setLayoutInfo] = useState<BoxTreeLevelLayoutInfo | null>(
      null,
    );

    const currentOrientation =
      hocProps.getChildrenOrientation?.(props.parentNode) ??
      CHILDREN_ORIENTATION_DEFAULT;
    const childrenOrientation =
      hocProps.getChildrenOrientation?.(props.node) ??
      CHILDREN_ORIENTATION_DEFAULT;

    const updateLayoutInfo = () => {
      const childRoots = Array.from(childRootRefs.values() ?? []);

      setLayoutInfo((oldInfo) =>
        keepIdentityIfEqual<BoxTreeLevelLayoutInfo | null>(eqDeepStrict)(
          oldInfo,
        )(
          rootRef.current === null || !nonEmptyArray.isNonEmpty(childRoots)
            ? null
            : {
                rootSize:
                  rootRef.current[
                    attributesByOrientation[childrenOrientation].size
                  ],
                children: pipe(
                  childRoots,
                  nonEmptyArray.map((el) => ({
                    size: el[attributesByOrientation[childrenOrientation].size],
                    position:
                      el[attributesByOrientation[childrenOrientation].position],
                  })),
                ),
              },
        ),
      );
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: stable value
    const linesLayoutResizeObserver = useMemo(
      () => new ResizeObserver(updateLayoutInfo),
      [],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: not sure how it was intended to work
    useLayoutEffect(() => {
      updateLayoutInfo();
      const rootCurrent = rootRef.current;
      const wrapperCurrent = wrapperRef.current;
      const perform = (fn: 'observe' | 'unobserve') => {
        if (rootCurrent !== null) {
          linesLayoutResizeObserver[fn](rootCurrent);
          props.linesLayoutResizeObserver?.[fn](rootCurrent);
        }
        if (wrapperCurrent !== null) {
          props.linesLayoutResizeObserver?.[fn](wrapperCurrent);
        }
      };
      perform('observe');
      return () => {
        perform('unobserve');
      };
    }, [props.node]);

    const getChildPath = useMemoCollection(
      (index: number): BoxTreeNodePath => [...props.path, index],
      [props.path],
    );

    const childrenContext = useChildrenContext(
      props.context,
      props.node,
      props.path,
    );

    return (
      <div
        className={classNames(
          'flex items-start',
          currentOrientation === 'horizontal' ? 'pr-2.5' : 'pb-2.5',
          childrenOrientation === 'horizontal' ? 'flex-col' : 'flex-row',
          props.topLevelLine &&
            `box-tree__top-level-line--${currentOrientation}`,
        )}
        ref={wrapperRef}
      >
        <div ref={mergeRefs(props.rootRef, rootRef)}>
          <hocProps.nodeComponent
            context={props.context}
            node={props.node}
            path={props.path}
          />
        </div>
        <BoxTreeLevelConnectionLines
          linesStyle={hocProps.getChildrenLinesStyle?.(
            props.node,
            childrenContext,
            props.path,
          )}
          layoutInfo={layoutInfo}
          orientation={childrenOrientation}
        />
        {children.length > 0 && (
          <div
            className={classNames(
              'relative flex',
              childrenOrientation === 'horizontal' ? 'flex-row' : 'flex-col',
            )}
          >
            {children.map((child, index) => (
              <BoxSubtree
                context={childrenContext}
                // biome-ignore lint/suspicious/noArrayIndexKey: no better key
                key={index}
                linesLayoutResizeObserver={linesLayoutResizeObserver}
                node={child}
                parentNode={props.node}
                path={getChildPath(index)}
                rootRef={setChildRootRef(index)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };
  return memo(BoxSubtree);
};
